import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Divider,
  Alert,
  Autocomplete,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Rating,
  CircularProgress,
} from '@mui/material';
import { CallMadeOutlined, ExpandMore, WarningAmber } from '@mui/icons-material';
import OrderFormLayout from '../../components/orders/OrderFormLayout';
import OutboundItemsTable, {
  OutboundOrderItem,
  AvailableItem,
} from '../../components/orders/OutboundItemsTable';
import { api } from '../../services/api';
import { Partner, OrderDirection } from '../../types/api';

interface UnfulfilledItem {
  item_id: number;
  item_name: string;
  category: string;
  unit: string;
  order_qty: number;
  shipped_qty: number;
  unfulfilled_qty: number;
  fulfill_quantity?: number;
}

interface UnfulfilledOrder {
  id: number;
  direction: string;
  partner_id: number;
  partner_name: string;
  status: string;
  created_at: string;
  items: UnfulfilledItem[];
}

interface PlannedShipmentItem {
  item_id: number;
  quantity: number;
}

interface PlannedShipment {
  order_id: number;
  items: PlannedShipmentItem[];
}

interface OrderMetadata {
  note?: string;
  transport_provider?: string;
  planned_previous_orders_shipment?: PlannedShipment[];
}

interface OrderItem {
  id: number;
  item_id: number;
  item_name: string;
  category: string;
  unit: string;
  order_qty: number;
  pending_qty: number;
  note?: string,
}

interface OrderDetail {
  id: number;
  direction: string;
  partner_id: number;
  partner_name: string;
  status: string;
  metadata: OrderMetadata;
  items: OrderItem[];
}

const OutboundOrderEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState<Partner | null>(null);
  const [remark, setRemark] = useState('');
  const [selectedTransport, setSelectedTransport] = useState<string>('');
  
  // Customers state
  const [customers, setCustomers] = useState<Partner[]>([]);
  
  // Items state
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([]);
  const [orderItems, setOrderItems] = useState<OutboundOrderItem[]>([]);

  // Unfulfilled orders state
  const [unfulfilledOrders, setUnfulfilledOrders] = useState<UnfulfilledOrder[]>([]);
  const [loadingUnfulfilled, setLoadingUnfulfilled] = useState(false);

  // Load order data, customers, and items on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      setInitialLoading(true);
      try {
        const [orderRes, customersRes, itemsRes] = await Promise.all([
          api.get(`/api/orders/${id}`),
          api.get('/api/partners', { params: { type: 'CUSTOMER' } }),
          api.get('/api/items'),
        ]);
        
        const orderData: OrderDetail = orderRes.data;
        const customersData = customersRes.data.partners || [];
        const itemsData = itemsRes.data.items || [];
        
        setCustomers(customersData);
        setAvailableItems(itemsData.map((item: any) => ({
          ...item,
          units_per_carton: item.metadata?.units_per_carton ?? 0,
        })));
        
        // Find and set the customer
        const customer = customersData.find((c: Partner) => c.id === orderData.partner_id);
        setSelectedCustomer(customer || null);
        
        // Set metadata fields
        setRemark(orderData.metadata?.note || '');
        setSelectedTransport(orderData.metadata?.transport_provider || '');
        
        // Convert order items to form items
        const formItems: OutboundOrderItem[] = orderData.items.map((item) => ({
          item_id: item.item_id,
          item_name: item.item_name,
          currentStock: null, // Will be loaded separately
          orderQuantity: item.order_qty,
          pendingQuantity: item.pending_qty,
          prevPendingQuantity: null,
          note: item.note || '',
          loadingStock: true, // Loading stock
        }));
        
        setOrderItems(formItems);
        
        // Fetch stock for all order items
        const itemIds = orderData.items.map((item) => item.item_id).filter((id) => id > 0);
        if (itemIds.length > 0) {
          try {
            const stockRes = await api.get(`/api/items/stock/${itemIds.join(',')}`);
            const stocksData = stockRes.data?.stocks || [];
            
            // Create a map of item_id -> available_quantity
            const stockMap = new Map<number, number>();
            stocksData.forEach((stock: { item_id: number; available_quantity: number }) => {
              stockMap.set(stock.item_id, stock.available_quantity);
            });
            
            // Update formItems with stock data (add back the order quantity since it's already reserved)
            const itemsWithStock = formItems.map((item) => ({
              ...item,
              // Stock to show = available_quantity + orderQuantity (since orderQuantity is already reserved for this order)
              currentStock: stockMap.has(item.item_id) 
                ? (stockMap.get(item.item_id) || 0) + item.orderQuantity
                : null,
              loadingStock: false,
            }));
            setOrderItems(itemsWithStock);
          } catch (err) {
            console.error('Error fetching stock:', err);
            // Set loadingStock to false even on error
            setOrderItems(formItems.map((item) => ({ ...item, loadingStock: false })));
          }
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load order data');
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  const handleFulfillQuantityChange = (orderId: number, itemId: number, value: string) => {
    // Allow only digits
    const onlyDigits = value.replace(/[^\d]/g, '');
    const numValue = onlyDigits === '' ? 0 : Number(onlyDigits);
    
    setUnfulfilledOrders((prev) =>
      prev.map((order) => {
        if (order.id !== orderId) return order;
        return {
          ...order,
          items: order.items.map((item) => {
            if (item.item_id !== itemId) return item;
            const validValue = Math.min(Math.max(0, numValue), item.unfulfilled_qty);
            return { ...item, fulfill_quantity: validValue };
          }),
        };
      })
    );
  };

  const validateForm = (): boolean => {
    if (!selectedCustomer) {
      setError('Customer is required');
      return false;
    }

    const selectedItems = orderItems.filter((item) => item.item_id > 0);
    
    if (selectedItems.length === 0) {
      setError('Please add at least one item');
      return false;
    }

    // Check if ALL selected items have either Order Qty or Pending Qty as non-zero
    const invalidItems = selectedItems.filter(
      (item) => item.orderQuantity === 0 && item.pendingQuantity === 0
    );
    
    if (invalidItems.length > 0) {
      setError('All items must have either Order Qty or Pending Qty as non-zero');
      return false;
    }

    const invalidStockItems = orderItems.filter(
      (item) =>
        item.item_id > 0 &&
        item.orderQuantity > 0 &&
        item.currentStock !== null &&
        item.orderQuantity > item.currentStock
    );

    if (invalidStockItems.length > 0) {
      setError('Order quantity cannot exceed available stock');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    // Build shipment_for_previous_orders from unfulfilled orders
    const shipmentForPreviousOrders = unfulfilledOrders
      .map((order) => {
        const itemsWithQuantity = order.items
          .filter((item) => item.fulfill_quantity && item.fulfill_quantity > 0)
          .map((item) => ({
            item_id: item.item_id,
            quantity: item.fulfill_quantity,
          }));
        
        if (itemsWithQuantity.length === 0) return null;
        
        return {
          order_id: order.id,
          items: itemsWithQuantity,
        };
      })
      .filter((order): order is { order_id: number; items: { item_id: number; quantity: number | undefined }[] } => order !== null);

    const payload = {
      direction: OrderDirection.OUTWARD,
      partner_id: selectedCustomer?.id,
      metadata: {
        ...(remark && { note: remark }),
        ...(selectedTransport && { transport_provider: selectedTransport }),
      },
      items: orderItems
        .filter((item) => item.item_id > 0)
        .map((item) => ({
          item_id: item.item_id,
          order_qty: item.orderQuantity,
          pending_qty: item.pendingQuantity,
          note: item.note,
        })),
      ...(shipmentForPreviousOrders.length > 0 && { shipment_for_previous_orders: shipmentForPreviousOrders }),
    };

    try {
      await api.put(`/api/orders/${id}`, payload);
      navigate(`/customers/orders/${id}`);
    } catch (err: any) {
      setError(err.message|| 'Failed to update order');
      console.error('Error updating order:', err);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <OrderFormLayout
      title="Edit Outward Order"
      icon={<CallMadeOutlined sx={{ fontSize: 32 }} />}
      onSubmit={handleSubmit}
      submitLabel="Update Order"
      loadingLabel="Updating..."
      loading={loading}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Customer Selection - Disabled for editing */}
      <Autocomplete
        fullWidth
        options={customers}
        getOptionLabel={(option) => option.name}
        value={selectedCustomer}
        disabled // Customer cannot be changed
        renderInput={(params) => (
          <TextField
            {...params}
            label="Customer"
            required
            helperText="Customer cannot be changed"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {selectedCustomer && selectedCustomer.rating != null && selectedCustomer.rating > 0 && (
                    <Rating value={selectedCustomer.rating} size="small" readOnly sx={{ mr: 1 }} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={{ mb: 3 }}
      />

      <Divider sx={{ my: 3 }} />

      {/* Unfulfilled Orders Section */}
      {selectedCustomer && unfulfilledOrders.length > 0 && (
        <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              bgcolor: 'warning.50',
              borderRadius: 1,
              '&.Mui-expanded': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmber color="warning" />
              <Typography fontWeight={600}>
                Previous Unfulfilled Orders
              </Typography>
              <Chip
                label={`${unfulfilledOrders.length} order${unfulfilledOrders.length > 1 ? 's' : ''}`}
                size="small"
                color="warning"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {unfulfilledOrders.map((order) => (
              <Box key={order.id} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Order #{order.id}
                  </Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ borderTop: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Unfulfilled Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Fulfill Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map((item) => (
                        <TableRow key={item.item_id} hover>
                          <TableCell>{item.item_name}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.unfulfilled_qty}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="text"
                              inputMode="numeric"
                              size="small"
                              value={item.fulfill_quantity || ''}
                              onChange={(e) =>
                                handleFulfillQuantityChange(
                                  order.id,
                                  item.item_id,
                                  e.target.value
                                )
                              }
                              inputProps={{
                                min: 0,
                                max: item.unfulfilled_qty,
                                style: { textAlign: 'center', width: 80 },
                              }}
                              placeholder="0"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {loadingUnfulfilled && selectedCustomer && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Checking for unfulfilled orders...
        </Typography>
      )}

      {/* Items Table with Search, Stock & Quantities */}
      <OutboundItemsTable
        items={orderItems}
        availableItems={availableItems}
        onItemsChange={setOrderItems}
        partnerId={selectedCustomer?.id}
      />

      <Divider sx={{ my: 3 }} />

      {/* Transport Preference (Optional) */}
      {selectedCustomer && (selectedCustomer.metadata?.preferred_transports?.length ?? 0) > 0 && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="transport-label">Transport Preference (Optional)</InputLabel>
          <Select
            labelId="transport-label"
            value={selectedTransport}
            label="Transport Preference (Optional)"
            onChange={(e) => setSelectedTransport(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {selectedCustomer.metadata?.preferred_transports?.map((transport: string) => (
              <MenuItem key={transport} value={transport}>
                {transport}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Remark */}
      <TextField
        fullWidth
        label="Order Notes"
        multiline
        rows={2}
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        placeholder="Add any special instructions or notes"
      />
    </OrderFormLayout>
  );
};

export default OutboundOrderEdit;
